<template>
  <div class="field" :class="modifiers">
    <label
      v-if="label"
      :for="id"
      class="field__label"
      :class="{ field__label_required: required }"
    >
      <slot name="label" />
      {{ label }}
    </label>
    <div class="field__container">
      <slot v-if="$slots.field" name="field" />
      <component
        :is="use"
        v-else-if="use"
        :id="id"
        ref="el"
        v-bind="attributes"
        v-on="listeners"
      />
      <input
        v-else-if="tag === FIELD_TYPES.INPUT"
        :id="id"
        ref="el"
        class="field__el"
        v-bind="attributes"
        v-on="listeners"
      >
      <textarea
        v-else-if="tag === FIELD_TYPES.TEXTAREA"
        :id="id"
        ref="el"
        class="field__el"
        v-bind="attributes"
        v-on="listeners"
      />
      <select
        v-else-if="tag === FIELD_TYPES.SELECT"
        :id="id"
        ref="el"
        class="field__el"
        v-bind="attributes"
        v-on="listeners"
      >
        <option v-if="placeholder" value="" disabled v-text="placeholder" />
        <option
          v-for="(option, i) in options"
          :key="`option_${i}`"
          :value="option.value"
          v-text="option.label"
        />
        <slot name="options" />
      </select>
      <BaseIcon v-if="Boolean(isComplete && showStatus && !error && !isFocus)" name="status" class="field__status" />
<!--       <div
        v-if="Boolean(isComplete && showStatus && !error && !isFocus)"
        class="field__status field__status_complete"
      /> -->
<!--       <div
        v-if="Boolean(error && showStatus && !isFocus)"
        class="field__status field__status_error"
      /> -->
      <div
        class="field__message"
        :class="{ field__message_active: Boolean(message && isFocus) }"
      >
        {{ message }}
      </div>
<!--       <input
        v-if="showPicker"
        type="color"
        :value="value"
        class="field__picker"
        @input="input"
      > -->
      <slot name="container" />
    </div>
    <slot />
  </div>
</template>

<script>
import IMask from 'imask'; // https://imask.js.org/guide.html
// import masks from '@/helpers/masks';
import BaseIcon from '@/components/BaseIcon.vue';

const FIELD_TYPES = Object.freeze({
  TEXTAREA: 'textarea',
  SELECT: 'select',
  INPUT: 'input',
});

const masks = {
  tel: {
    mask: '+{7}(000)000-00-00',
  },
  number: {
    mask: Number,
  },
  // email: {
  //   mask: /^.+\\@.+\..+$/i,
  // },
  // email: {
  //   mask: 'w@w.w',
  //   blocks: {
  //     w: { mask: /\w*/g }, // but probably should be different for each part...
  //   },
  // },
  // email(value) {
  //   if (/^[a-z0-9_\\.-]+$/.test(value)) return true;
  //   if (/^[a-z0-9_\\.-]+@$/.test(value)) return true;
  //   if (/^[a-z0-9_\\.-]+@[a-z0-9-]+$/.test(value)) return true;
  //   if (/^[a-z0-9_\\.-]+@[a-z0-9-]+\.$/.test(value)) return true;
  //   if (/^[a-z0-9_\\.-]+@[a-z0-9-]+\.[a-z]{1,4}$/.test(value)) return true;
  //   if (/^[a-z0-9_\\.-]+@[a-z0-9-]+\.[a-z]{1,4}\.$/.test(value)) return true;
  //   if (/^[a-z0-9_\\.-]+@[a-z0-9-]+\.[a-z]{1,4}\.[a-z]{1,4}$/.test(value)) return true;
  //   return false;
  // },
};

export default {
  name: 'BaseField',
  components: {
    BaseIcon,
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Array],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: [Object, String],
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
    parseValue: {
      type: Function,
      default: (val) => val,
    },
    use: {
      type: [String, Object],
      default: '',
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
    // showPicker: {
    //   type: Boolean,
    //   default: false,
    // },
  },
  data() {
    return {
      isOverflowing: false,
      isFocus: false,
      imask: null,
      message: '',
    };
  },
  staticData: () => ({
    FIELD_TYPES,
  }),
  computed: {
    id() {
      return `field_${this._uid}`;
    },
    tag() {
      if (this.$slots.options || this.options.length) return FIELD_TYPES.SELECT;
      if (this.type) return FIELD_TYPES.INPUT;
      return FIELD_TYPES.TEXTAREA;
    },
    withCustomSelect() {
      return Boolean(this.use);
    },
    attributes() {
      const attrs = {
        value: this.value,
        disabled: Boolean(this.disabled),
        ...this.$attrs,
      };
      if (this.type) attrs.type = this.type;
      if (this.placeholder) attrs.placeholder = this.placeholder;
      if (this.maxlength) attrs.maxlength = this.maxlength;
      if (this.withCustomSelect) attrs.options = this.options;
      return attrs;
    },
    listeners() {
      const listeners = { ...this.$listeners };
      const addition = {
        input: this.input,
        focus: this.focus,
        blur: this.blur,
      };
      if (!this.withCustomSelect) {
        listeners.input = addition.input;
        listeners.focus = listeners.focus ? [listeners.focus, addition.focus] : addition.focus;
        listeners.blur = listeners.blur ? [listeners.blur, addition.blur] : addition.blur;
      } else {
        listeners.open = listeners.open ? [listeners.open, addition.focus] : addition.focus;
        listeners.close = listeners.close ? [listeners.close, addition.blur] : addition.blur;
      }
      return listeners;
    },
    modifiers() {
      return {
        field_overflowing: this.isOverflowing,
        field_disabled: this.disabled,
        field_required: this.required,
        field_complete: this.isComplete,
        field_focus: this.isFocus,
        field_error: Boolean(this.error || (this.value && !this.isComplete)),
        [`field_size_${this.size}`]: Boolean(this.size),
        [`field_${this.tag}`]: true,
      };
    },
    isComplete() {
      if (this.isFocus) return false;
      if (this.required && Array.isArray(this.value) && this.value.length === 0) return false;
      if ((this.tag === FIELD_TYPES.SELECT || !this.imask) && this.value) return true;
      return this.imask && this.value && this.imask.masked.isComplete;
    },
  },
  watch: {
    value(val) {
      this.update(val);
    },
    error(message) {
      this.setMessage(message);
    },
  },
  mounted() {
    if (this.mask) {
      this.initMask();
    }
  },
  beforeDestroy() {
    if (this.imask) {
      this.imask.destroy();
    }
  },
  methods: {
    input(e) {
      this.update(this.withCustomSelect ? e : e.target.value);
    },
    focus() {
      this.isFocus = true;
      this.$emit('focus');
    },
    blur() {
      this.isFocus = false;
      this.$emit('blur');
    },
    update(val) {
      const value = this.parseValue(val);
      if (this.imask) {
        this.imask.value = value;
        this.$emit('input', this.imask.value);
      } else {
        this.$emit('input', value);
      }
      this.checkOverflowing();
    },
    setFocus() {
      if (this.$refs.el) this.$refs.el.focus();
      return this;
    },
    delFocus() {
      if (this.$refs.el) this.$refs.el.blur();
      return this;
    },
    initMask() {
      const mask = typeof this.mask === 'string' ? masks[this.mask] : this.mask;
      if (mask) {
        this.imask = new IMask(this.$refs.el, mask);
        this.update(this.value);
      }
    },
    setMessage(message) {
      this.message = message;
      return this;
    },
    checkOverflowing() {
      const { el } = this.$refs;
      this.isOverflowing = el && el.scrollWidth > el.clientWidth;
    },
    checkComplete() {
      return this.isComplete;
    },
  },
};
</script>

<style lang="stylus">
.field
  display block

  &__label
    display inline-block
    vertical-align top
    font-size 0.8em
    letter-spacing .01em
    color #000
    margin-bottom .5em

    &_required
      &:after
        content '*'
        color $c_error
        margin 0 0 0 .15em
        ~/_complete &
        ~/_disabled &
          display none

  &__message
    abs bottom 100% right
    width calc(100% + 10px)
    min-width 200px
    min-width 32ch
    max-width 100vw
    background-color $c_error//$c_bg//#f9d7ad//#ffe5c4
    padding .8em 1.2em
    font-size 14px
    line-height 1.3
    color #fff//darken(@background-color, 85%)
    border-radius 4px 4px 12px 4px
    margin 0 -10px 7px 0
    transform translate(0, 10px)
    transition opacity .2s ease, transform .2s cubic-bezier(.455,.03,.515,.955)
    pointer-events none
    visibility hidden
    // box-shadow 0 0 30px alpha(#000, 25%)
    opacity 0
    z-index 30
    &:after
      content ''
      abs top 100% right
      width 0
      height 0
      border solid transparent
      border-width 7px 7px 0 7px
      border-top-color @background-color
      margin -3px 4px 0 0
      z-index 1
    &_active
      visibility visible
      opacity 1
      transform translate(0, 0)

  &__container
    position relative
    ~/_overflowing:not(^[0]_textarea) &
      &:after
        content ''
        abs top bottom right
        width 3em
        background linear-gradient(90deg, alpha(#fff, 10%) 0%, #fff 100% )
        pointer-events none
        margin 1px
        margin-right 1em
        z-index 1

  // &__picker
  //   display block
  //   abs bottom right
  //   width 16px
  //   height @width
  //   border 0
  //   padding 0
  //   margin 2px
  //   appearance none
  //   outline none
  //   cursor pointer
  //   background none
  //   &::-webkit-color-swatch-wrapper
  //     padding 2px
  //     background none
  //   &::-webkit-color-swatch
  //     border 0
  //     box-shadow inset 0 0 0 1px alpha(#000, 15%)
  //     border-radius @width

  &__status
    abs top right
    width 1em
    height @width
    color $c_success
    pointer-events none
    background-color #fff
    border-radius 50%
    margin 0.4em
    z-index 2

    // &_complete
    //   width 8px
    //   height 5px
    //   border solid $c_success
    //   border-width 0 0 1px 1px
    //   margin 4px 6px
    //   transform rotate(-45deg)

    // &_error
    //   width 2px
    //   height 9px
    //   margin 6px 7px
    //   &:after
    //   &:before
    //     content ''
    //     abs top bottom left right
    //     background-color $c_error
    //   &:before
    //     margin-bottom 3px
    //   &:after
    //     margin-top @height - @width

  &__el
    display block
    width 100%
    max-width 100%
    outline none
    font-family inherit
    font-size 0.9em
    color $c_text
    font-style normal
    font-weight 400
    text-transform none
    line-height 1
    height 2.8em//50px
    padding 0 1em
    background-color #fff
    border solid 1px #c2c2c2
    border-radius 0.5em
    // box-shadow 1px 1px 3px alpha($c_text, 10%)
    // transition border .2s ease-in, box-shadow .2s ease-in
    transition border .2s ease-in
    -webkit-appearance none

    &:focus
      outline none !important
      border-color $c_bright
      //box-shadow 1px 1px 3px alpha(@border-color, 30%)

    // ~/_size_xs &
    //   font-size 14px
    //   height 30px

    // ~/_size_s &
    //   font-size 14px
    //   height 37px

    ~/_complete &:not(:focus)
     border-color $c_success
     //box-shadow 1px 1px 3px alpha(@border-color,20%)

    ~/_error &:not(:focus):not([disabled])
    ~/_error .multiselect__tags
      border-color $c_error
      // box-shadow 1px 1px 3px alpha(@border-color,20%)

    input[type=number]&
      -moz-appearance textfield // Firefox
      &::-webkit-outer-spin-button
      &::-webkit-inner-spin-button
        -webkit-appearance none
        margin 0

    input&
    textarea&
      &::placeholder
        font-family inherit
        color alpha($c_dim, 40%)
        font-size inherit
        font-style normal
        font-weight 400
        text-overflow ellipsis
        line-height normal
        opacity 1

      &[disabled]
        &::placeholder
          color alpha($c_dim, 0%)

    textarea&
      height auto
      min-height 6em
      padding-top 12px
      padding-bottom @padding-top
      resize vertical
      overflow-x hidden
      overflow-y auto
      line-height 1.2

    select&
      padding-right 25px
      background @background-color url('data:image/svg+xml,%3Csvg width="24" height="14" viewBox="0 0 24 14" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath fill="%23ddd" d="M8 8l4 6 4-6H8zM16 6l-4-6-4 6h8z"/%3E%3C/svg%3E') no-repeat right center
      text-indent 0.01px
      text-overflow ''
      appearance none
      cursor pointer
      optgroup
        font-weight 700
      &::-ms-expand
        display none
</style>
